// Service for fetching and searching Material Symbols, Emojis, and Custom Icons
import materialSymbols from '../material_symbols.json';
import { getIconManifest } from './iconService';

let symbolsCache = null;
let emojiCache = null;
let customIconsCache = null;

// Load Material Symbols from local JSON file
const fetchMaterialSymbols = async () => {
  if (symbolsCache) return symbolsCache;

  // Import is synchronous, but we keep the async pattern for consistency
  symbolsCache = materialSymbols;
  return symbolsCache;
};

// Fetch emoji data from emojibase CDN
const fetchEmojis = async () => {
  if (emojiCache) return emojiCache;

  try {
    const response = await fetch('https://unpkg.com/emojibase-data@latest/en/compact.json');
    const data = await response.json();
    // Data format: [{unicode: "😀", label: "grinning face", ...}, ...]
    emojiCache = data.map(item => ({
      emoji: item.unicode,
      name: item.label
    }));
    return emojiCache;
  } catch (error) {
    console.error('Failed to fetch emojis:', error);
    return [];
  }
};

// Fetch custom icons from Firestore
const fetchCustomIcons = async () => {
  if (customIconsCache) return customIconsCache;

  try {
    const manifest = await getIconManifest();
    // Convert manifest object to array of icon names
    customIconsCache = Object.keys(manifest).map(name => ({
      name,
      version: manifest[name]
    }));
    return customIconsCache;
  } catch (error) {
    console.error('Failed to fetch custom icons:', error);
    return [];
  }
};

// Search symbols, emojis, and custom icons
export const searchIcons = async (query) => {
  if (!query || query.trim().length === 0) return [];

  const lowerQuery = query.toLowerCase().trim();
  const results = [];

  // Search Custom Icons (highest priority)
  const customIcons = await fetchCustomIcons();
  const matchingCustomIcons = customIcons
    .filter(icon => icon.name.toLowerCase().includes(lowerQuery))
    .slice(0, 5)
    .map(icon => ({
      type: 'custom',
      value: `{c.${icon.name}}`,
      display: icon.name,
      icon: icon.name,
      iconName: icon.name
    }));

  // Search Material Symbols
  const symbols = await fetchMaterialSymbols();
  const matchingSymbols = symbols
    .filter(name => name.toLowerCase().includes(lowerQuery))
    .slice(0, 8)
    .map(name => ({
      type: 'symbol',
      value: `{${name}}`,
      display: name,
      icon: name
    }));

  // Search Emojis
  const emojis = await fetchEmojis();
  const matchingEmojis = emojis
    .filter(item => item.name.toLowerCase().includes(lowerQuery))
    .slice(0, 7)
    .map(item => ({
      type: 'emoji',
      value: item.emoji, // Insert the exact emoji from emojibase
      display: item.name,
      icon: item.emoji
    }));

  results.push(...matchingCustomIcons, ...matchingSymbols, ...matchingEmojis);
  return results.slice(0, 20);
};

// Clear cache (useful after uploading new custom icons)
export const clearCustomIconsCache = () => {
  customIconsCache = null;
};
